import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BreadcrumbsItem } from '../../../core/components/organism/Breadcrumbs';
import { ExerciseAction, IExercise } from '../../../core/models/exercise';
import { getDisciplineName } from '../../../core/utils/new_discipline';
import { getExercisesList, saveExerciseAnswer } from '../queries/exercises';
import { useExerciseStore } from '../stores/exercisesStore';
import { getModuleLabel } from '../utils/contentHelper';

export const useDisciplineExerciseHook = () => {
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [initialPage, setInitialPage] = useState<number>();
  const [isContentModal, setIsContentModal] = useState(false);
  const [rightAnswer, setRightAnswer] = useState<boolean>(false);

  const { discipline, module, subject, slug } = useParams();
  const { exercisesList, setExercisesList } = useExerciseStore();

  const { data, isSuccess, isPending } = useQuery({
    queryKey: ['fetch-discipline-exercises', module, subject, slug],
    queryFn: () => getExercisesList(module || '', slug || ''),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const breadcrumbItems: BreadcrumbsItem[] = [
    {
      label: discipline ? getDisciplineName(discipline).replace('Humana', '') : '',
      href: `/disciplinas/${discipline}`,
    },
    {
      label: getModuleLabel(module || ''),
      href: `/disciplinas/${discipline}/modulo/${module}/tipo/exercicio`,
      active: true,
    },
    // {
    //   label: initialPage && data && data[initialPage] ? data[initialPage].title : '',
    //   href: '#',
    // },
  ];

  useEffect(() => {
    if (isSuccess) {
      handleSuccess(data);
    }
  }, [isSuccess, slug]);

  useEffect(() => {
    if (rightAnswer) {
      setTimeout(function () {
        setRightAnswer(false);
      }, 7000);
    }
  }, [rightAnswer]);

  const handleSuccess = (data: IExercise[]) => {
    const exercises = setCurrentExercise(data);

    setInitialPage(getCurrentExerciseIndex(exercises));

    setExercisesList(exercises);

    if (!startDate) setStartDate(new Date());
  };

  const getCurrentExerciseIndex = (exercises: IExercise[]) => {
    return exercises.findIndex((exercise) => exercise.slug === slug);
  };

  const setCurrentExercise = (exercises: IExercise[]) => {
    const stateExercises = exercises.length > 0 ? exercises : exercisesList;
    const currentExercise = exercises[getCurrentExerciseIndex(exercises)];

    const updatedExercises = stateExercises.map((stateExercise) => {
      stateExercise.selected = stateExercise.id === currentExercise.id;

      if (stateExercise.id === currentExercise.id) {
        return { ...stateExercise, ...currentExercise, done: false };
      }
      return stateExercise;
    });
    return updatedExercises;
  };

  const changeExercise = (index: number) => {
    const newSlug = exercisesList[index].slug;

    navigate(
      `/disciplinas/${discipline}/modulo/${module}/assunto/${subject}/exercicio/${newSlug}`
    );
  };

  const getSelectedAlternatives = (exercise: IExercise) => {
    const selectedAlternativesIds = exercise.alternatives
      .filter((alternative) => alternative.selected)
      .map((alternative) => alternative.id);

    return selectedAlternativesIds;
  };

  function dispatchSetContentAsDoneEvent(id: string, isCorrect: boolean) {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: id,
        isCorrect,
      },
    });
    window.dispatchEvent(setContentAsDone);
  }

  const updateExercise = async (
    exercise: IExercise,
    action: ExerciseAction = null
  ) => {
    const updatedExercises = exercisesList.map((stateExercise) =>
      stateExercise.id === exercise.id
        ? { ...stateExercise, ...exercise }
        : stateExercise
    );

    setExercisesList(updatedExercises);

    if (action === 'verifyAnswer') {
      const selectedAlternativesIds = getSelectedAlternatives(exercise);
      const endDate = new Date();
      const time = Math.abs(startDate!.getTime() - endDate.getTime());

      if (exercise.correct) {
        setRightAnswer(true);
      }

      await saveExerciseAnswer(
        exercise.slug,
        selectedAlternativesIds,
        exercise.correct,
        time
      );

      dispatchSetContentAsDoneEvent(exercise.id, exercise.correct);
    }
  };


  return {
    slug,
    module,
    subject,
    isLoading: isPending,
    discipline,
    initialPage,
    isContentModal,
    breadcrumbItems,
    exercises: exercisesList,
    rightAnswer,
    updateExercise,
    changeExercise,
    setIsContentModal,
  };
};
